// Worker
// Utilities
import { extractEntityRoutingIdFromSubRoute, extractMyTasksRoutingDetailsFromSubRoute, extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { ViewDataFetchState, ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { DataPreFetcherEventNames, PreFetchedDataKeys } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
import { MyDayUser } from "@ms/uno-constants/lib/local/StoreConstants";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
import { ActionCreator } from "@ms/uno-actioncreators/lib/local/actioncreators/ActionCreator";
/**
 * Data fetch worker
 */ export class DataFetchWorker {
    init() {
        if (this.initialized) {
            return;
        }
        // Fetch the data for the initial route if the app is loaded in foreground.
        if (!this.configProvider().flights.EnableDeferDataFetchForBackgroundLoad || !this.appContextStore.isAppInBackground()) {
            this.fetchDataForTheInitialRoute();
        }
        if (this.configProvider().flights.EnableDataFetchViaWorker) {
            // Subscribe to routeStore to fetch view data based on route change,
            // not waiting on the respective view to be loaded
            this.routeStore.subscribeToStore(this.fetchViewDataBasedOnRoute);
            if (this.configProvider().flights.EnableDeferDataFetchForBackgroundLoad) {
                this.appContextStore.subscribeToStore(this.handleAppBackgroundStateUpdate);
            }
        }
        this.initialized = true;
        this.wasAppInBackground = this.appContextStore.isAppInBackground();
    }
    /**
     * Stops the worker by removing the event listeners
     */ stop() {
        this.routeStore.unsubscribeFromStore(this.fetchViewDataBasedOnRoute);
        this.appContextStore.unsubscribeFromStore(this.fetchViewDataBasedOnRoute);
        this.initialized = false;
        this.lastRoute = undefined;
    }
    /**
     * Fetch the data for the initial route
     */ async fetchDataForTheInitialRoute() {
        if (this.configProvider().flights.EnableDataPreFetcher) {
            // Wait for data pre-fetch completion before triggering data fetch for initial route
            await this.waitForDataPreFetchCompletion();
        }
        if (this.configProvider().flights.EnableDataFetchViaWorker) {
            this.fetchViewDataBasedOnRoute();
            if (this.shouldFetchDataForLeftNav()) {
                const leftNavViewAc = this.acLoader.load(ActionCreator.LeftNavView);
                leftNavViewAc.fetchViewData();
            }
        }
    }
    /**
     * Check if the data should be fetched for the left nav
     */ shouldFetchDataForLeftNav() {
        const { clientFlavor } = this.configProvider().sessionMetaData.appMetadata;
        const subRouteType = this.routeStore.getCurrentSubRouteType();
        return clientFlavor !== ClientFlavor.TaskFromMessage && clientFlavor !== ClientFlavor.Tab && subRouteType !== SubRouteType.ConfigTeamsTab && subRouteType !== SubRouteType.RemoveTeamsTab;
    }
    /**
     * Check if data should be fetched on the current route change
     */ shouldFetchData() {
        // If the app is in background, we should not fetch the data
        if (this.appContextStore.isAppInBackground()) return false;
        const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
        const currentSubRoute = this.routeStore.getCurrentSubRoute();
        const hasSubRouteTypeChanged = currentSubRouteType !== this.lastRoute?.subRouteType;
        const entityIdInLastRoute = this.lastRoute && extractEntityRoutingIdFromSubRoute(this.lastRoute.subRoute, this.lastRoute.subRouteType);
        const entityIdInCurrentRoute = extractEntityRoutingIdFromSubRoute(currentSubRoute, currentSubRouteType);
        const hasNavigatedBetweenTwoPlans = !hasSubRouteTypeChanged && entityIdInCurrentRoute != null && entityIdInLastRoute != null && entityIdInCurrentRoute !== entityIdInLastRoute;
        // Has the app moved to foreground (current state - app in foreground) from background (previous state app was in background)
        const hasAppMovedToForeground = !this.appContextStore.isAppInBackground() && this.wasAppInBackground;
        // 1. Typically, only subroute change should not trigger data fetch to avoid unnecessary service call.
        // 2. When routing between two same plan types, the subRouteType will not change, but the planId may change and we need to fetch data.
        // 3. Task route in BasicPlan doesn't need any special handling as it is not handled by BasicPlanViewActionCreator.
        // 4. MyTasksViewActionCreator fetches data based on the subRoute, so data must be fetched on every MyTasks subroute change.
        // 5. App moves from background to foreground
        const shouldFetchData = hasSubRouteTypeChanged || hasNavigatedBetweenTwoPlans || currentSubRouteType === SubRouteType.MyTasks || hasAppMovedToForeground;
        return shouldFetchData;
    }
    /**
     * Fetch data for the MyDay view
     */ fetchMyDayViewData() {
        const myDayViewAc = this.acLoader.load(ActionCreator.MyDayView);
        const forceFetch = this.consumeViewForceFetchState(ViewName.MyDayPage, MyDayUser.CURRENT_USER);
        myDayViewAc.fetchViewData(forceFetch);
    }
    /**
     * Fetch data for the MyTasks view
     */ fetchMyTasksViewData() {
        const currentSubRoute = this.routeStore.getCurrentSubRoute();
        const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(currentSubRoute, SubRouteType.MyTasks);
        if (!myTasksRoutingDetails) {
            return;
        }
        const myTasksViewAc = this.acLoader.load(ActionCreator.MyTasksView);
        /**
         * MyTasks view load is the initialLoad if any of the following conditions are met:
         * 1. It it the initial route i.e lastRoute is undefined
         * 2. The last route is not MyTasks view
         * 3. The last route is MyTasks view but have current route doesn't have taskId in it i.e. it is a pivot change
         */ const isInitialLoad = this.lastRoute === undefined || this.lastRoute.subRouteType !== SubRouteType.MyTasks || !myTasksRoutingDetails.task;
        const forceFetch = this.consumeViewForceFetchState(ViewName.MyTasksPage, this.appContextStore.getAppContext().userId ?? "");
        myTasksViewAc.fetchViewData(myTasksRoutingDetails.pivotType, isInitialLoad, forceFetch);
    }
    /**
     * Fetch data for the MyPlans view
     */ fetchMyPlansViewData() {
        const myPlansViewwAc = this.acLoader.load(ActionCreator.MyPlansView);
        myPlansViewwAc.fetchViewData();
    }
    /**
     * Fetch data for the MyPortfolios view
     */ fetchMyPortfoliosViewData() {
        const myPortfoliosViewAc = this.acLoader.load(ActionCreator.MyPortfoliosView);
        myPortfoliosViewAc.fetchViewData();
    }
    /**
     * Fetch data for the BasicPlan view
     */ fetchPlanViewData() {
        const subRouteType = this.routeStore.getCurrentSubRouteType();
        const subRoute = this.routeStore.getCurrentSubRoute();
        const planRouingDetails = extractPlanRoutingDetailsFromSubRoute(subRoute, subRouteType);
        if (!planRouingDetails) {
            return;
        }
        const basicPlanViewAc = this.acLoader.load(ActionCreator.BasicPlanView);
        const forceFetch = this.consumeViewForceFetchState(ViewName.BasicPlanPage, planRouingDetails.planId);
        basicPlanViewAc.fetchViewData(planRouingDetails.planId, forceFetch);
    }
    /**
     * Update the last route
     */ updateLastRoute() {
        const route = {
            rootUrl: "",
            subRouteType: this.routeStore.getCurrentSubRouteType(),
            subRoute: this.routeStore.getCurrentSubRoute()
        };
        this.lastRoute = route;
    }
    /**
     * Update the app background state
     */ updateAppBackgroundState() {
        this.wasAppInBackground = this.appContextStore.isAppInBackground();
    }
    /**
     * Subscribe to data pre-fetcher completion event
     */ async waitForDataPreFetchCompletion() {
        // If pre-fetch is already completed, store the pre-fetched data
        if (window.dataPreFetchCompleted) {
            this.storeCompletePreFetcherResult();
            this.clearPreFetcherResult();
            return;
        }
        this.subscribeToIndividualDataPreFetchEvents();
        // Subscribe to pre-fetcher completion event to wait on it
        await new Promise((resolve)=>{
            window.addEventListener(DataPreFetcherEventNames.FetchCompleted, ()=>{
                this.clearPreFetcherResult();
                resolve();
            }, {
                once: true
            });
        });
    }
    /**
     * Subscribe to individual data pre-fetch events
     */ subscribeToIndividualDataPreFetchEvents() {
        const eventKeyMap = {
            [DataPreFetcherEventNames.FetchedMruData]: PreFetchedDataKeys.Mru,
            [DataPreFetcherEventNames.FetchedMyDayTasks]: PreFetchedDataKeys.MyDayTasks,
            [DataPreFetcherEventNames.FetchedATMTasks]: PreFetchedDataKeys.ATMTasks,
            [DataPreFetcherEventNames.FetchedPlan]: PreFetchedDataKeys.Plan,
            [DataPreFetcherEventNames.FetchedTTTabs]: PreFetchedDataKeys.TTTabs,
            [DataPreFetcherEventNames.FetchedBuckets]: PreFetchedDataKeys.Buckets,
            [DataPreFetcherEventNames.FetchedBasicPlanTasks]: PreFetchedDataKeys.BasicPlanTasks
        };
        for (const [eventName, key] of Object.entries(eventKeyMap)){
            window.addEventListener(eventName, ()=>{
                this.handleIndividualDataPreFetchEvent(key);
            });
        }
    }
    /**
     * Store complete pre-fetcher result
     */ storeCompletePreFetcherResult() {
        const fetchedData = window.dataPreFetcherResult;
        if (fetchedData) {
            const dataPreFetchActionCreator = this.acLoader.load(ActionCreator.DataPreFetch);
            for (const key of Object.keys(fetchedData)){
                dataPreFetchActionCreator.storePrefetchedServiceResult(PreFetchedDataKeys[key], fetchedData);
            }
        }
    }
    /**
     * Handle individual data pre-fetch events
     * @param key The pre-fetched data key
     */ handleIndividualDataPreFetchEvent(key) {
        const dataPreFetchActionCreator = this.acLoader.load(ActionCreator.DataPreFetch);
        window.dataPreFetcherResult && dataPreFetchActionCreator.storePrefetchedServiceResult(key, window.dataPreFetcherResult);
    }
    /**
     * Clear the pre-fetcher result
     */ clearPreFetcherResult() {
        if (window.dataPreFetcherResult) {
            window.dataPreFetcherResult = undefined;
        }
    }
    /**
     * method to get the current force fetch status for the view and reset it as needed
     * @param viewName name of the view
     * @param viewId id of the view
     */ consumeViewForceFetchState(viewName, viewId) {
        /**
         * Force fetch will be needed in below cases:
         * 1. Pre fetch failed for the route, force fetch needed to get the latest data from service, force fetch set to false on default route setup.
         * 2. Route change in running/cached sessions where data is present in memory but data is not latest.
         * 3. Delta token expiry where fresh data is needed in running sessions to ensure latest data for the user.
         * 1 is taken care of with the window check. 2&3 are handled by the store check.
         */ const forceFetch = this.didPrefetchFailForView(viewName, viewId) || this.performanceCollectionStore.getViewForceDataFetchState(viewName, viewId);
        if (forceFetch) {
            this.acLoader.load(ActionCreator.PerformanceCollection).viewForceFetch(viewName, viewId, false);
            window.dataPreFetchFailedForView = undefined;
        }
        return forceFetch;
    }
    /**
     * method to check if preFetch failed for a given viewName and viewId
     * @param viewName name of the view
     * @param viewId id of the view
     */ didPrefetchFailForView(viewName, viewId) {
        return window.dataPreFetchViewName === viewName && window.dataPreFetchViewId === viewId && window.dataPreFetchFailedForView === true;
    }
    /**
     * Constructor for the DataFetchWorker
     * @param acLoader The action creator loader
     * @param storeLoader The store loader
     * @param loggers The logger context
     * @param configProvider The config provider
     */ constructor(acLoader, storeLoader, loggers, configProvider){
        this.acLoader = acLoader;
        this.storeLoader = storeLoader;
        this.loggers = loggers;
        this.configProvider = configProvider;
        /** Indicates if the worker has been initialized */ this.initialized = false;
        /** The route store */ this.routeStore = this.storeLoader.load(Store.Route);
        /** The last route */ this.lastRoute = undefined;
        /** Last state for app (background/foreground) */ this.wasAppInBackground = false;
        this.performanceCollectionStore = this.storeLoader.load(Store.PerformanceCollection);
        this.appContextStore = this.storeLoader.load(Store.AppContext);
        /**
     * Fetch the data for the background state
     */ this.handleAppBackgroundStateUpdate = async ()=>{
            if (!this.configProvider().flights.EnableDeferDataFetchForBackgroundLoad) {
                return;
            }
            // Has the app moved to foreground (current state - app in foreground) from background (previous state app was in background)
            const hasAppMovedToForeground = !this.appContextStore.isAppInBackground() && this.wasAppInBackground;
            // Update the local state on the worker. Update it after resolving hasAppMovedToForeground to ensure the correct state is captured
            this.updateAppBackgroundState();
            // Fetch the data when the app is transitioning states from background to foreground
            if (hasAppMovedToForeground) {
                this.fetchViewDataBasedOnRoute();
                if (this.shouldFetchDataForLeftNav()) {
                    const leftNavViewAc = this.acLoader.load(ActionCreator.LeftNavView);
                    leftNavViewAc.fetchViewData();
                }
            }
        };
        /**
     * Fetch view data based on the current route
     */ this.fetchViewDataBasedOnRoute = async ()=>{
            if (!this.shouldFetchData()) {
                return;
            }
            const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
            this.loggers.performanceMarker.mark(MarkerTag.ViewDataFetch, {
                state: ViewDataFetchState[ViewDataFetchState.Started]
            });
            switch(currentSubRouteType){
                case SubRouteType.MyDay:
                    this.fetchMyDayViewData();
                    break;
                case SubRouteType.MyTasks:
                    this.fetchMyTasksViewData();
                    break;
                case SubRouteType.MyPlans:
                    this.fetchMyPlansViewData();
                    break;
                case SubRouteType.MyPortfolios:
                    this.fetchMyPortfoliosViewData();
                    break;
                case SubRouteType.BasicPlan:
                case SubRouteType.TodoList:
                    this.fetchPlanViewData();
                    break;
                default:
                    break;
            }
            this.updateLastRoute();
            this.updateAppBackgroundState();
        };
    }
}
